import { useCallback, useEffect, useState } from "react";
import { Bell, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const KIND = {
  stampa: ["Stampa", "bg-amber-500/15 text-amber-400 border-amber-500/30"],
  whatsapp: ["WhatsApp", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"],
  report: ["Report", "bg-blue-500/15 text-blue-400 border-blue-500/30"],
  concorso: ["Concorso", "bg-violet-500/15 text-violet-300 border-violet-500/30"],
};
const KIND_FILTERS = [["", "Tutti"], ["stampa", "Stampa"], ["whatsapp", "WhatsApp"], ["report", "Report"], ["concorso", "Concorso"], ["sede", "Fuori sede"]];
const PERIODS = [[1, "24 ore"], [7, "7 giorni"], [30, "30 giorni"], [0, "Sempre"]];

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function Chip({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${active ? "bg-primary/15 text-primary border-primary/40" : "bg-secondary/40 text-muted-foreground border-border hover:text-foreground"}`}>
      {children}
    </button>
  );
}

function AlertRow({ a }) {
  const [label, cls] = KIND[a.entity_type] || ["Sistema", "bg-slate-500/15 text-slate-300 border-slate-500/30"];
  const isAlarm = /allarme|rifiutat|offline/i.test(a.entity_name || "");
  return (
    <li className="py-2 flex items-start gap-3 text-sm" data-testid={`alert-${a.id}`}>
      <span className="text-[11px] font-mono-code text-muted-foreground w-24 shrink-0">{fmt(a.created_at)}</span>
      <Badge className={`border shrink-0 ${cls}`}>{label}</Badge>
      <span className={`min-w-0 ${isAlarm ? "text-amber-300 font-medium" : ""}`}>
        {a.entity_name}
        {a.details?.tipo === "riepilogo_chiusura" && <span className="text-muted-foreground"> · riepilogo chiusura ({a.details.quando}): {a.details.non_ritirati} premi non ritirati, {a.details.inviate?.length ?? 0} email</span>}
      </span>
    </li>
  );
}

export function AlertsPanel() {
  const [rows, setRows] = useState(null);
  const [kind, setKind] = useState("");
  const [days, setDays] = useState(30);
  const load = useCallback(() => api.recentAlerts(20, kind, days).then((r) => setRows(r.alerts)).catch(() => setRows([])), [kind, days]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="p-5 bg-card border-border space-y-3" data-testid="alerts-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Bell className="h-4 w-4" /> Ultimi allarmi e notifiche</h3>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={load} data-testid="alerts-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="alerts-filters">
        {KIND_FILTERS.map(([k, l]) => <Chip key={k || "all"} active={kind === k} onClick={() => setKind(k)} testId={`alerts-kind-${k || "all"}`}>{l}</Chip>)}
        <span className="mx-1 h-4 w-px bg-border" />
        {PERIODS.map(([d, l]) => <Chip key={d} active={days === d} onClick={() => setDays(d)} testId={`alerts-days-${d}`}>{l}</Chip>)}
      </div>
      {rows === null ? null : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="alerts-empty">Nessuna notifica per i filtri scelti.</p>
      ) : (
        <ul className="divide-y divide-border/60">{rows.map((a) => <AlertRow key={a.id} a={a} />)}</ul>
      )}
    </Card>
  );
}
